import { ActionList, ActionMenu } from '@primer/react';
import { ChevronDownIcon } from '@primer/octicons-react';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { truongApi } from '../../api/truongApi';
import { useCommonContext } from '../../contexts/common';
import { dm_khoi } from '../../models/response/category/dm_khoi';
import { actions } from '../../state/actions/actionsWrapper';
import { RootState } from '../../state/reducers';

type ComboboxKhoiProps = {
	isReadonly?: boolean;
	disabled?: boolean;
	requireTruong?: boolean;
	value?: number;
	onValueChanged: (id: number, data?: dm_khoi) => void;
	className?: string;
	isShowClearButton?: boolean;
	preText?: string;
	width?: string | number;
	dm_truong_id?: number;
	stylingMode?: 'outlined' | 'filled' | 'underlined';
	size?: 'small' | 'medium' | 'large';
};

const ComboboxKhoi = (props: ComboboxKhoiProps) => {
	const { dm_khois } = useSelector((state: RootState) => state.categorySource);
	const { language } = useSelector((x: RootState) => x.common);
	const dispatch = useDispatch();
	const { translate } = useCommonContext();
	const [truongKhois, setTruongKhois] = useState<dm_khoi[]>([]);
	const [loadedTruongId, setLoadedTruongId] = useState<number>(0);

	useEffect(() => {
		if (dm_khois.length === 0) dispatch(actions.categorySource.loadKhoiStart());
	}, [dm_khois.length, dispatch]);

	useEffect(() => {
		let cancelled = false;
		const truongId = Number(props.dm_truong_id) || 0;

		if (truongId > 0) {
			setLoadedTruongId(0);
			truongApi
				.selectKhoi(truongId)
				.then((res) => {
					if (cancelled) return;
					if (res && res.is_success && Array.isArray(res.data)) {
						setTruongKhois(res.data);
					} else {
						setTruongKhois([]);
					}
					setLoadedTruongId(truongId);
				})
				.catch(() => {
					if (cancelled) return;
					setTruongKhois([]);
					setLoadedTruongId(truongId);
				});
		} else {
			setTruongKhois([]);
			setLoadedTruongId(0);
		}

		return () => {
			cancelled = true;
		};
	}, [props.dm_truong_id]);

	useEffect(() => {
		const truongId = Number(props.dm_truong_id) || 0;
		const currentValue = Number(props.value) || 0;

		if (currentValue > 0 && truongId > 0 && loadedTruongId === truongId) {
			const khoiBelongsToTruong = truongKhois.some((khoi) => khoi.id === currentValue);
			if (!khoiBelongsToTruong) {
				props.onValueChanged(0);
			}
		}
	}, [props.dm_truong_id, props.value, loadedTruongId, truongKhois]);

	const source: dm_khoi[] = useMemo(() => {
		const truongId = Number(props.dm_truong_id) || 0;
		if (truongId > 0) {
			if (loadedTruongId === truongId) {
				return truongKhois;
			}
			return dm_khois.filter((x) => x.dm_truong_id == truongId);
		}
		if (props.requireTruong) {
			return [];
		}
		return dm_khois;
	}, [dm_khois, loadedTruongId, props.dm_truong_id, props.requireTruong, truongKhois]);

	const selectedData = useMemo(() => {
		if (props.value && dm_khois.length > 0) {
			// Tìm trong toàn bộ list để edit mode hiển thị đúng khi dm_truong_id chưa set
			return dm_khois.find((x) => x.id == props.value);
		}
		if (props.value && truongKhois.length > 0) {
			return truongKhois.find((x) => x.id == props.value);
		}
		return undefined;
	}, [dm_khois, truongKhois, props.value]);

	return (
		<ActionMenu>
			<ActionMenu.Button
				aria-label="Select school year"
				size={props.size}
				disabled={props.disabled || props.isReadonly}
				style={{
					width: props.width ?? '100%',
					display: 'flex',
					justifyContent: 'space-between',
					opacity: (props.disabled || props.isReadonly) ? 0.6 : 1,
					cursor: (props.disabled || props.isReadonly) ? 'not-allowed' : 'pointer',
				}}
				className='button-left'
				trailingAction={ChevronDownIcon}
			>
				{selectedData ? (language === "en" ? selectedData.ten_khoi_en : selectedData.ten_khoi) : (props.preText || translate("KhoiCombobox.PlaceHolder"))}
			</ActionMenu.Button>
			<ActionMenu.Overlay width="small">
				<ActionList selectionVariant="single">
					{props.isShowClearButton &&
						<ActionList.Item key={0} selected={props.value != undefined && 0 === props.value}
							onSelect={() => {
								props.onValueChanged(0)
							}}
						>
							{language === "en" ? "Select" : "Chọn khối"}
						</ActionList.Item>
					}
					{source && source.map((item) => {
						return (
							<ActionList.Item key={item.id} selected={props.value != undefined && item.id === props.value}
								onSelect={() => {
									props.onValueChanged(item.id)
								}}
							>
								{language === "en" ? item.ten_khoi_en : item.ten_khoi}
							</ActionList.Item>
						);
					})}
				</ActionList>
			</ActionMenu.Overlay>
		</ActionMenu>
	);
};

export default ComboboxKhoi;
